/**
 * API service for User Notes
 * Allows users to add notes to devices, locations, entities, and links
 */

import axios from 'axios';

const baseUrl = process.env.REACT_APP_API_URL || '/api';

// ==================== Types ====================

export type EntityType = 'device' | 'location' | 'entity' | 'link' | 'geofence';
export type NotePriority = 'low' | 'normal' | 'high' | 'critical';

export interface Note {
  id: number;
  entity_type: EntityType;
  entity_id: string;
  entity_label?: string;
  title?: string;
  content: string;
  tags: string[];
  priority: NotePriority;
  latitude?: number;
  longitude?: number;
  reference_date?: string;
  created_by: string;
  created_at: string;
  updated_at?: string;
  is_deleted: boolean;
}

export interface NoteCreate {
  entity_type: EntityType;
  entity_id: string;
  entity_label?: string;
  title?: string;
  content: string;
  tags?: string[];
  priority?: NotePriority;
  latitude?: number;
  longitude?: number;
  reference_date?: string;
}

export interface NoteUpdate {
  title?: string;
  content?: string;
  tags?: string[];
  priority?: NotePriority;
  entity_label?: string;
  latitude?: number;
  longitude?: number;
  reference_date?: string;
}

export interface NoteListResponse {
  items: Note[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface NoteTag {
  id: number;
  tag_name: string;
  tag_category?: string;
  usage_count: number;
  created_at: string;
}

export interface NoteTagListResponse {
  items: NoteTag[];
  total: number;
}

export interface NoteChoices {
  entity_types: string[];
  priorities: string[];
  existing_tags: string[];
}

export interface NoteSearchParams {
  entity_type?: EntityType;
  entity_id?: string;
  created_by?: string;
  priority?: NotePriority;
  tag?: string;
  search?: string;
  include_deleted?: boolean;
  page?: number;
  page_size?: number;
}

// ==================== Helper ====================

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`
});

// ==================== API Functions ====================

/**
 * Get dropdown choices for note form
 */
export const getChoices = async (): Promise<NoteChoices> => {
  const response = await axios.get(`${baseUrl}/notes/choices`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

/**
 * Create a new note
 */
export const createNote = async (note: NoteCreate): Promise<Note> => {
  const response = await axios.post(`${baseUrl}/notes`, note, {
    headers: getAuthHeaders()
  });
  return response.data;
};

/**
 * List notes with optional filters
 */
export const listNotes = async (params: NoteSearchParams = {}): Promise<NoteListResponse> => {
  const response = await axios.get(`${baseUrl}/notes`, {
    params,
    headers: getAuthHeaders()
  });
  return response.data;
};

/**
 * Get notes for a specific entity
 */
export const getNotesForEntity = async (entityType: EntityType, entityId: string): Promise<Note[]> => {
  const response = await axios.get(`${baseUrl}/notes/entity/${entityType}/${encodeURIComponent(entityId)}`, {
    headers: getAuthHeaders()
  });
  return response.data.items;
};

/**
 * Get a specific note by ID
 */
export const getNote = async (id: number): Promise<Note> => {
  const response = await axios.get(`${baseUrl}/notes/${id}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

/**
 * Update an existing note
 */
export const updateNote = async (id: number, note: NoteUpdate): Promise<Note> => {
  const response = await axios.put(`${baseUrl}/notes/${id}`, note, {
    headers: getAuthHeaders()
  });
  return response.data;
};

/**
 * Delete a note (soft delete by default)
 */
export const deleteNote = async (id: number, permanent: boolean = false): Promise<void> => {
  await axios.delete(`${baseUrl}/notes/${id}`, {
    params: { permanent },
    headers: getAuthHeaders()
  });
};

/**
 * List all available tags
 */
export const listTags = async (category?: string, search?: string): Promise<NoteTagListResponse> => {
  const response = await axios.get(`${baseUrl}/notes/tags/all`, {
    params: { category, search },
    headers: getAuthHeaders()
  });
  return response.data;
};

/**
 * Create a new tag
 */
export const createTag = async (tagName: string, tagCategory?: string): Promise<NoteTag> => {
  const response = await axios.post(`${baseUrl}/notes/tags`, {
    tag_name: tagName,
    tag_category: tagCategory
  }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ==================== Attachments ====================

export interface NoteAttachment {
  id: number;
  note_id: number;
  filename: string;
  original_filename: string;
  file_size: number;
  mime_type: string;
  storage_type: string;
  uploaded_by: string;
  uploaded_at: string;
  description?: string;
  download_url: string;
  is_image: boolean;
}

export interface AppendInsightsResult {
  success: boolean;
  message: string;
  note_id: number;
  insights?: string;
}

/** Upload a file attachment to a note */
export const uploadNoteAttachment = async (
  noteId: number,
  file: File,
  description?: string
): Promise<NoteAttachment> => {
  const formData = new FormData();
  formData.append('file', file);
  if (description) formData.append('description', description);
  const response = await axios.post(`${baseUrl}/note-attachments/${noteId}`, formData, {
    headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

/** List attachments for a note */
export const getNoteAttachments = async (noteId: number): Promise<NoteAttachment[]> => {
  const response = await axios.get(`${baseUrl}/note-attachments/${noteId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

/** Delete an attachment */
export const deleteNoteAttachment = async (attachmentId: number): Promise<void> => {
  await axios.delete(`${baseUrl}/note-attachments/${attachmentId}`, {
    headers: getAuthHeaders()
  });
};

/**
 * Download an attachment. Fetches as a blob (so the bearer token is sent) and
 * triggers a browser download using the original filename.
 */
export const downloadNoteAttachment = async (
  attachmentId: number,
  filename: string
): Promise<void> => {
  const response = await axios.get(`${baseUrl}/note-attachments/${attachmentId}/download`, {
    headers: getAuthHeaders(),
    responseType: 'blob'
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/** Fetch an attachment as an object URL (for inline image preview) */
export const getNoteAttachmentObjectUrl = async (attachmentId: number): Promise<string> => {
  const response = await axios.get(`${baseUrl}/note-attachments/${attachmentId}/download`, {
    headers: getAuthHeaders(),
    responseType: 'blob'
  });
  return window.URL.createObjectURL(new Blob([response.data]));
};

/** Extract text + AI insights from an attachment and append them to the parent note */
export const appendInsightsToNote = async (attachmentId: number): Promise<AppendInsightsResult> => {
  const response = await axios.post(
    `${baseUrl}/note-attachments/${attachmentId}/append-insights`,
    null,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// ==================== Enrichment (Tavily research) ====================

export interface EnrichmentStatus {
  enabled: boolean;
  message: string;
}

export interface EnrichmentSource {
  title: string;
  url: string;
  snippet: string;
}

export interface EnrichmentResult {
  success: boolean;
  query: string;
  summary: string;
  sources: EnrichmentSource[];
  error?: string;
}

/** Check whether Tavily web-search enrichment is configured */
export const getEnrichmentStatus = async (): Promise<EnrichmentStatus> => {
  try {
    const response = await axios.get(`${baseUrl}/enrichment/status`, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch {
    return { enabled: false, message: 'unavailable' };
  }
};

/** Freeform web-search enrichment used by the Intel Packet research chat */
export const enrichSearch = async (
  query: string,
  searchDepth: 'basic' | 'advanced' = 'basic',
  maxResults: number = 5
): Promise<EnrichmentResult> => {
  const response = await axios.post(`${baseUrl}/enrichment/search`, {
    query,
    search_depth: searchDepth,
    max_results: maxResults
  }, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ==================== Constants ====================

export const NOTE_ENTITY_TYPES: EntityType[] = ['device', 'location', 'entity', 'link', 'geofence'];
export const NOTE_PRIORITIES: NotePriority[] = ['low', 'normal', 'high', 'critical'];

export const PRIORITY_COLORS: Record<NotePriority, string> = {
  low: '#9e9e9e',      // Gray
  normal: '#2196f3',   // Blue
  high: '#ff9800',     // Orange
  critical: '#f44336'  // Red
};

export const PRIORITY_LABELS: Record<NotePriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  critical: 'Critical'
};

export default {
  getChoices,
  createNote,
  listNotes,
  getNotesForEntity,
  getNote,
  updateNote,
  deleteNote,
  listTags,
  createTag,
  uploadNoteAttachment,
  getNoteAttachments,
  deleteNoteAttachment,
  downloadNoteAttachment,
  getNoteAttachmentObjectUrl,
  appendInsightsToNote,
  getEnrichmentStatus,
  enrichSearch,
  NOTE_ENTITY_TYPES,
  NOTE_PRIORITIES,
  PRIORITY_COLORS,
  PRIORITY_LABELS
};

